import { computed } from 'vue';
import { groupPackageRows } from '@/helpers/service-package-rows';
import { getPartDuration } from '@/modules/calendar/appointment/helpers';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useServicesStore } from '@/stores/services';
import type { AppointmentPartAttributes, ServicePackage } from '@/types';

// The parts list is rendered as rows: one per plain part, or one per package selection.
// The grouping itself is shared with the register, see groupPackageRows.

export type PartRow =
  | { key: string; kind: 'single'; partIndexes: [number] }
  | {
      key: string;
      kind: 'package';
      partIndexes: number[];
      servicePackage: ServicePackage;
    };

export const groupParts = (
  parts: AppointmentPartAttributes[],
  packageForPart: (
    part: AppointmentPartAttributes,
  ) => ServicePackage | undefined,
): PartRow[] =>
  groupPackageRows(parts, packageForPart).map((row) =>
    row.kind === 'single'
      ? { key: row.key, kind: row.kind, partIndexes: row.indexes }
      : {
          key: row.key,
          kind: row.kind,
          partIndexes: row.indexes,
          servicePackage: row.servicePackage,
        },
  );

// Anything that depends on the number of cards (e.g. whether they can be dragged) should count
// these rows, not the parts
export const usePartRows = () => {
  const { formData } = useCreateAppointmentStore();
  const { servicePackageByServicePackageServiceId } = useServicesStore();

  const rows = computed(() =>
    groupParts(formData.partsAttributes, (part) =>
      servicePackageByServicePackageServiceId(part.servicePackageServiceId),
    ),
  );

  return { rows };
};

// The parts of one package row, by their live indexes into formData.partsAttributes
export const usePackageParts = (partIndexes: () => number[]) => {
  const { formData } = useCreateAppointmentStore();

  const parts = computed(() =>
    partIndexes().map((partIndex) => formData.partsAttributes[partIndex]),
  );

  const totalDuration = computed(() =>
    parts.value.reduce((sum, part) => sum + getPartDuration(part), 0),
  );

  return { parts, totalDuration };
};
